/**
 * @file アプリケーション全体で使用される定数定義
 *
 * API URL定義（ApiUrl）とビジネスロジック定数（Constants）を集約。
 * - ApiUrl: BFF APIのエンドポイントURL（認証系・参照系・更新系に分類）
 * - Constants: 支払方法コード、オプションステータス、VoIP関連定数、
 *   事業者ID、キャンペーンステータス、付加サービス情報など
 *
 * アプリケーション全体から参照される中心的な定数ファイル。
 */

/** コードと名称のペア型（支払方法・VoIP事業者等で使用） */
export type CodeAndName = {
  code: string;
  name: string;
  nameShort?: string;
};

/** VoIP付加サービスのパッケージ型（サービスパック選択肢） */
export type Package = {
  type: string;
  name: string;
  referenceMark: boolean;
  explanation: string;
  price: string;
};

/** VoIP付加サービスの単品型（番号表示・キャッチ電話等の個別オプション） */
export type Individually = {
  type: string;
  id: string;
  name: string;
  referenceMark: boolean;
  toolTip: string;
  construction: string;
  price: string;
};

export namespace ApiUrl {
  /** ドメイン */
  export const DOMAIN = process.env.NEXT_PUBLIC_BFF_URL;

  /** 認証API */
  export namespace Authn {
    /** マイページログアウト */
    export const LOGOUT = '/api/authn/logout';
    /** クレデンシャル更新 */
    export const RENEW = '/api/authn/renew';
  }

  /** 非認証API */
  export namespace NonProtected {
    /** マイページログイン */
    export const LOGIN = '/api/v1/login';
    /** SMS認証 */
    export const SMS_AUTH = '/api/v1/fvno/password-reset/sms-auth';
    /** ワンタイムパスワード検証 */
    export const VALIDATE_ONETIME_PASSWORD = '/api/v1/password-reset/otp/verification';
    /** パスワードリセット */
    export const PASSWORD_RESETTING = '/api/v1/fvno/password-reset';
    /** SSO用Credential取得 */
    export const CREDENTIAL = '/api/v1/fvno/sso/credential';
  }

  /** 参照系API */
  export namespace Read {
    /** ユーザー情報取得 */
    export const USER_INFO = '/api/read/v1/user';
    /** 契約者情報取得 */
    export const CONTRACT_INFO = '/api/read/v2/contract';
    /** 自動通知情報取得 */
    export const AUTO_NOTIFICATION_INFO = '/api/read/v1/fvno/auto-notification-info';
    /** 契約プラン情報取得 */
    export const PLAN_INFO = '/api/read/v2/contract/plan';
    /** キャンペーン情報取得 */
    export const CAMPAIGN_INFO = '/api/read/v1/fvno/campaign';
    /** 請求情報取得(インヴォイス対応前) */
    export const BILLING_INFO = '/api/read/v1/billing';
    /** 請求情報取得(インヴォイス対応後) */
    export const BILLING_INFO_INVOICE = '/api/read/v1/billing_invoice';
    /** 支払情報取得 */
    export const PAYMENT_INFO = '/api/read/v2/payment';
    /** オプション情報取得 */
    export const OPTIONS_INFO = '/api/read/v1/fvno/option';
    /** オプション詳細取得(NURO SAFE) */
    export const NURO_SAFE = '/api/read/v1/option/nurosafe';
    /** オプション詳細取得(McAfee) */
    export const MCAFEE_INFO = '/api/read/v1/option/mcafee';
    /** オプション詳細取得(kaspersky) */
    export const KASPERSKY = '/api/read/v1/option/kaspersky';
    /** オプション詳細取得(sumainokaketsuke) */
    export const SUMAINO_KAKETSUKE = '/api/read/v1/option/sumainokaketsuke';
    /** オプション詳細取得(kurashinokenko) */
    export const KURASHINO_KENKO = '/api/read/v1/option/kurashinokenko';
    /** オプション詳細取得(Seiton&Hozon) */
    export const SEITON_HOZON = '/api/read/v1/option/seiton-hozon';
    /** オプション詳細取得(らくらくバックアップ無制限) */
    export const RAKURAKU_BACKUP = '/api/read/v1/option/rakurakubackup';
    /** オプション詳細取得(超ホーダイ) */
    export const CHO_HODAI = '/api/read/v1/option/chohodai';
    /** オプション詳細取得(TokutokuDelikitch) */
    export const TOKUTOKU_DELIKITCH = '/api/read/v1/option/tokutokudelikitch';
    /** オプション詳細取得(OsaifuSupport) */
    export const OSAIFU_SUPPORT = '/api/read/v1/option/osaifusupport';
    /** オプション詳細取得(安心パック) */
    export const ANSN_PACK = '/api/read/v1/fvno/option/ansn-pack';
    /** オプション詳細取得(安心パック２) */
    export const ANSN_PACK_2 = '/api/read/v1/fvno/option/ansn-pack-2';
    /** オプション詳細取得(GigaPC) */
    export const GIGA_PC = '/api/read/v1/fvno/option/giga-pc';
    /** オプション詳細取得(スピードパック) */
    export const SPEED_PACK = '/api/read/v1/fvno/option/speed-pack';
    /** オプション詳細取得(ゆれしる) */
    export const YURESIRU_INFO = '/api/read/v1/fvno/option/yuresiru-info';
    /** オプション詳細取得(NURO スマートホーム スタートプラン) */
    export const SMART_HOME = '/api/read/v1/option/smarthome';
    /** VoIP申込可否判定 */
    export const VOIP_OPTION_SUBSCRIPTION_CHECK = '/api/read/v1/option/voip/check';
    /** 請求書ダウンロード */
    export const DOWNLOAD_INVOICE = '/api/read/v1/download/invoice_pdf';
    /** 割賦総額ダウンロード */
    export const DOWNLOAD_INSTALLMENT = '/api/read/v1/download/installment';
    /** 工事承諾情報取得 */
    export const CONSTRUCTION_AGREEMENT_INFO = '/api/read/v1/construction-agreement';
    /** 注意事項情報取得 */
    export const CAUTION_INFO = '/api/read/v1/caution';
    /** 返還一覧取得 */
    export const BILLING_RLS_REFUND_LIST = '/api/read/v1/billing-rls-refund-list';
    /** 返還明細(PDF) */
    export const DOWNLOAD_REFUND = '/api/read/v1/download/refund';
    /** 解約時の契約解除料取得 */
    export const CANCELLATION_FEE = '/api/read/v1/cancellation-fee';
    /** FVNO提供形態取得 */
    export const FVNO_PROVIDE_TYPE = '/api/read/v1/fvno/provide-type';
    /** 切り替え先ポート情報取得 */
    export const CHG_DST_PORT_INFO = '/api/read/v1/chg-dst-port-info';
    /** ホワイトコール24申込書請求チェック */
    export const WC24_FORM_DEMAND_CHECK = '/api/read/v1/option/voip/wc24/form-demand-check';
    /** 10Gアップグレードトークン情報取得 */
    export const UPGRADE_TOKEN = '/api/read/v1/g10upgTokenInfo';
    /** トーケンステータスチェック */
    export const CHECK_TOKEN = '/api/read/v1/fvno/check-token';
    /** アドロック情報取得 */
    export const ADLOCK = '/api/read/v1/fvno/option/adlock';
    /** with パック情報取得 */
    export const WITH_PACK = '/api/read/v1/fvno/option/with-pack';
    /** アドロック (1ffv) 情報取得 */
    export const ADLOCK_1FFV = '/api/read/v1/fvno/option/adlock-1ffv';
  }

  /** 更新系API */
  export namespace Write {
    /** メールアドレス変更 */
    export const EMAIL = '/api/write/v1/contract/email/entry';
    /** 支払情報更新（クレジットカード） */
    export const CREDIT_CARD = '/api/write/v2/payment/credit-card';
    export const SECURE_CREDIT_CARD = '/api/write/v3/payment/credit-card';
    /** オプション情報更新 */
    export const OPTIONS_INFO = '/api/write/v1/fvno/option';
    /** VoIPオプション情報更新 */
    export const VOIP_OPTION_INFO = '/api/write/v2/option/voip';
    /** ホワイトコール24申込書請求 */
    export const WC24_FORM_DEMAND = '/api/write/v2/option/voip/wc24/form-demand';
    /** キャッシュバック口座登録 */
    export const CASHBACK_REGISTER = '/api/write/v2/regist-cashback-account';
    /** ID/PWD変更 */
    export const ID_PASSWORD_CHANGE = '/api/write/v1/user/id-password';
    /** ひかりTV登録 */
    export const HKARITV_ENTRY = '/api/write/v1/option/hikaritv/entry';
    /** メール・SMS配信停止／再開 */
    export const NOTIFICATION_DELIVER = '/api/write/v1/notification-deliver';
    /** 3Dセキュア認証連携パラメータ生成 */
    export const SECURE_ENCRYPT = '/api/write/v1/fvno/secure-encrypt';
    /** 3Dセキュア認証結果復号 */
    export const SECURE_DECRYPT = '/api/write/v1/fvno/secure-decrypt';
  }
}

export namespace Constants {
  /** APIタイプ */
  export namespace ApiType {
    export const API_GET = 'get';
    export const OPTION_CHANGE = 'optionChange';
    export const TIME_OUT = 'timeOut';
    export const API_POST = 'post';
  }

  /** エラーメッセージ */
  export namespace ErrorDialogMessage {
    export const DEFAULT = 'システムエラーが発生いたしました。\n大変ご不便をお掛けいたしますが、しばらく経ってから再度お試しください。';
    export const OPTION_CHANGE = '現在、お申込みを受け付けられない状態です。';
    export const TIME_OUT = '一定時間が経過したためログアウトしました。\nお手数ですが、最初からお試しください。';
  }

  /** 支払情報サービスリスト */
  export namespace PaymentMethod {
    export const BANK_TRANSFER: CodeAndName = {
      code: '10',
      name: '銀行振込',
      nameShort: '銀行振り込み',
    };
    export const BANK_TRANSFER_BEFORE_ACCOUNT_TRANSFER: CodeAndName = {
      code: '11',
      name: '口座振替前銀行振込',
      nameShort: '銀行振り込み',
    };
    export const ACCOUNT_TRANSFER: CodeAndName = {
      code: '20',
      name: '自動口座振替',
      nameShort: '口座振替',
    };
    export const CREDIT_CARD: CodeAndName = { code: '30', name: 'クレジットカード' };
    export const CONVENIENCE_STORE: CodeAndName = {
      code: '40',
      name: 'コンビニ払い',
      nameShort: 'コンビニ',
    };
    export const CONVENIENCE_STORE_BEFORE_ACCOUNT_TRANSFER: CodeAndName = {
      code: '41',
      name: '口座振替前コンビニ',
      nameShort: 'コンビニ',
    };
    export const ISP_RESELLER: CodeAndName = {
      code: '50',
      name: 'ISP・リセラー請求',
    };
    export const NTT_TEL: CodeAndName = {
      code: '60',
      name: 'NTT請求（電話料金合算）',
    };
    export const SB_TEL: CodeAndName = {
      code: '70',
      name: 'ソフトバンク請求（電話料金合算）',
      nameShort: 'ソフトバンクまとめて支払い',
    };
  }

  /** オプションステータス */
  export namespace OptionStatus {
    export const NONE = 'NONE';
    export const ENTRY = 'ENTR';
    export const SERVICE = 'SRVC';
    export const CANCEL = 'CNCL';
    export const QUIT = 'QUIT';
    export const QUIT_WITHOUT = 'QTWT';
  }

  /** VOIPステータス */
  export namespace VoipOptionStatus {
    export const NONE = '未申込';
    export const ENTRY = '申込中';
    export const SERVICE = '利用中';
    export const CANCEL = 'キャンセル';
    export const QUIT = '解約完了';
    export const QUIT_WITHOUT = '解約中';
    export const QUIT_REQUESTED = '解約手続き中';
  }

  // 電話サービス提供事業者リスト
  export namespace VoipList {
    export const NTT: CodeAndName = {
      code: '00',
      name: 'NTT加入電話',
    };
    export const NTT_LIGHT: CodeAndName = { code: '01', name: 'NTT加入電話ライトプラン' };
    export const SOFTBANK: CodeAndName = {
      code: '06',
      name: 'おとくライン / ホワイト光電話（ソフトバンク）',
    };
    export const HIKARI: CodeAndName = { code: '31', name: 'ひかり電話（NTT）※各社光コラボご利用中の方はこちら' };
    export const AU_CABLE_JCOM: CodeAndName = {
      code: '36',
      name: 'auひかり電話 / ケーブルプラス電話（KDDI）',
    };
    export const JCOM_PHONE_PLUS: CodeAndName = { code: '38', name: 'J:COM　PHONEプラス' };
    export const FUSION: CodeAndName = { code: '39', name: 'FUSION / 楽天モバイル' };
    export const COMMUFA: CodeAndName = { code: '73', name: 'コミュファ光電話（CTC）' };
    export const EO: CodeAndName = {
      code: '75',
      name: 'eo光電話（オプテージ ※旧ケイ・オプティコム）',
    };
    export const MEGAEGG: CodeAndName = {
      code: '76',
      name: 'メガ・エッグ光電話（エネコム）',
    };
    export const BBIQ: CodeAndName = { code: '78', name: 'BBIQ光電話（QTnet）' };
    export const HOME: CodeAndName = { code: '90', name: 'homeでんわ（NTTドコモビジネス）' };
    export const OTHERS: CodeAndName = { code: '99', name: 'その他' };
  }

  /** VoIP申込 */
  export namespace VoipEnter {
    export namespace Message {
      export const PACK1 = '番号表示、番号通知リクエスト、キャッチ電話、着信お断り通話、着信転送を、お申込みの場合は、パック１を選択してください。';
      export const PACK2 = 'キャッチ電話、着信お断り通話、着信転送を、お申込みの場合は、パック２を選択してください。';
      export const PACK3 = '番号表示、番号通知リクエスト、キャッチ電話、着信お断り通話を、お申込みの場合は、パック３を選択してください。';
      export const PACK4 = 'キャッチ電話、着信お断り通話を、お申込みの場合は、パック４を選択してください。';
      export const PACK_BASIC = '番号表示、番号通知リクエストを、お申込みの場合は、ベーシックパックを選択してください。';
      export const NOT_AVAILABLE_NUMBER_PORTABILITY = '番号ポータビリティは選択できません。';
      export const NG_STATUS = '現在、お申し込みを受け付けられない状態です。';
      export const CHANGE_DUPLICATE = '現在、付加サービスの変更の手続き中のため、お申込みいただけません。手続き完了までしばらくお待ちください。'; // F30W0006
      export const CHANGE_NG = '付加サービスの変更は、NURO光でんわが利用中でない場合、お申込みいただけません。'; // F30W0007
      export const CHANGE_FILE_NG = '付加サービスの変更処理中にエラーが発生しました。'; // F30W0008
    }
  }

  /** VoIP申込確認 */
  export namespace VoipEnterConfirm {
    export namespace Message {
      export const NUMBER_PORTABILITY_ERROR = '該当地域は番号ポータビリティの提供エリア外のため、お申込みいただけません。'; // F30W0003
      export const VOIP_STATUS_ERROR = '既にNURO光でんわの申込を受け付けています。手続き完了までしばらくお待ちください。'; // F30W0004
    }
  }

  /** オプションオーダータイプ */
  export namespace OptionOrderType {
    export const SIMPLE_ENTRY = 1;
    export const SIMPLE_ENTRY_CANCEL = 2;
    export const SIMPLE_QUIT = 3;
    export const SIMPLE_QUIT_CANCEL = 4;
    export const CONTENT_CHANGE = 5;
    export const SAME_TIME_ENTRY = 6;
  }

  /** オプションコード */
  export namespace OptionCode {
    // 追加・変更がある場合はoption.jsonも書き換える
    export const NURO_HIKARI_DENWA = 'VOIP';
    export const U_NEXT_WEEKLY = 'MO100N';
    export const U_NEXT_MONTHLY = 'MO101N';
    export const HIKARI_TV = 'HIKARITV';
    export const KASPERSKY = 'MO200N';
    export const TSUNAGARU_ASSURANCE = 'MO201N';
    export const KURASHINO_OMAMORI = 'MO202N';
    export const NURO_REMOTE_SUPPORT_MANSION = 'MO203N';
    export const ANSHIN_PACK_MANSION = 'MO204N';
    export const NURO_SUMAINO_KAKETSUKE_MANSION = 'MO205N';
    export const KURASHINO_KENKO_MANSION = 'MO206N';
    export const KURASHINO_SODAN_MANSION = 'MO207N';
    export const KURASHINO_BENRI_PACK = 'MO208N';
    export const KASPERSKY_STANDARD = 'MO209N';
    export const NURO_MARUGOTO_SUPPORT = 'MO210N';
    export const NURO_REMOTE_SUPPORT_HIGHEND = 'MO211N';
    export const VALED_LIFE = 'MO212N';
    export const KURASHINO_SODAN_HIGHEND = 'MO213N';
    export const KURASHINO_KENKO_HIGHEND = 'MO214N';
    export const NURO_SUMAINO_KAKETSUKE_HIGHEND = 'MO215N';
    export const HIKARI_TV_1 = 'MO216N';
    export const HIKARI_TV_2 = 'MO217N';
    export const RAKURAKU_BACKUP = 'MO232N';
    export const ITSUDEMO_KAKETSUKE = 'MO233N';
    export const ANSHIN_REPAIR = 'MO234N';
    export const OMAMORI_PACK_MANSION = 'MO235N';
    export const CHO_HODAI = 'MO237N';
    export const KURASHINO_OMAMORI_WIDE = 'MO238N';
    export const SEIKATSU_BENRI_PACK = 'MO239N';
    export const TOKUTOKU_DELIKITCH = 'MO256N';
    export const OSAIFU_SUPPORT = 'MO257N';
    export const SEIKATSU_OTASUKE_PACK = 'MO258N';
    export const KIKIHOSHO = 'MO259N';
    export const LIFE_SUPPORT_PACK = 'MO260N';
    export const NURO_DENKI = 'MO261N';
    export const MATOMETE_DENKI = 'MO262N';
    export const MIZUMORE_CHECKER = 'MO263N';
    export const NURO_GAS = 'MO264N';
    export const AMUE_LINK_ALL = 'MO265N';
    export const AMUE_LINK_INSTALLMENT = 'MO266N';
    export const MATOMETE_GAS = 'MO267N';
    export const SEITON_HOZON = 'MO268N';
    export const SMART_SET = 'MO269N';
    export const MCAFEE_MDS_FREE = 'MO270N';
    export const MCAFEE_MDS_PAID = 'MO271N';
    export const MCAFEE_OPS = 'MO272N';
    export const SMART_LOCK_SET = 'MO332N';
    export const SMART_CAMERA_SET = 'MO333N';
    export const VALED_LIFE_CONCIERGE = 'MO338N';
    export const NURO_SMART_HOME_START = 'MO339N';
    export const NURO_SMART_LIFE = 'MO340N';
    export const PS5 = 'MO341N';
    export const PS5_DIGITAL_EDITION = 'MO342N';
    export const NURO_HIKARI_SAFE = 'MO343N';
    export const NURO_HIKARI_SAFE_STANDARD = 'MO344N';
    export const NURO_SAKUTTO_SUPPORT = 'MO345N';
    export const TSUNAGARU_MESH_WIFI = 'MO352N';
    export const NURO_ENTRY_PACK = 'MO357N';
  }

  /** キャンペーンコード＆名称 */
  export namespace Cashback {
    export namespace StatusCode {
      export const BEFORE_RECEIPT_PERIOD = '0';
      export const COMPLETE = '1';
      export const EXPIRES = '2';
      export const EXTINGUISHMENT_OF_RIGHTS = '3';
      export const ERROR = '4';
      export const DURING_PROCEDURE = '5';
      export const BEFORE_PROCEDURE = '6';
      export const ACCOUNT_HOLDER_ILLEGAL = '7';
    }

    export namespace StatusName {
      export const BEFORE_RECEIPT_PERIOD = '受取可能期間前';
      export const COMPLETE = '入金完了';
      export const EXPIRES = '期限切れ';
      export const EXTINGUISHMENT_OF_RIGHTS = '権利消滅';
      export const ERROR = '入金エラー';
      export const DURING_PROCEDURE = '手続き中';
      export const BEFORE_PROCEDURE = '手続き前';
      export const ACCOUNT_HOLDER_ILLEGAL = '口座情報誤り';
    }
  }

  /** ホワイトリスト */
  export namespace PathList {
    export const NOT_LOGIN = [
      '/login',
      '/login/token',
      '/logout',
      '/password-reset',
      '/password-reset/onetime-password',
      '/password-reset/resetting',
      '/password-reset/complete',
      '/ssologin',
    ];
  }

  /** 日付判定 */
  export namespace DateOfSwitching {
    export const INVOICE = '202310';
  }

  /** 事業者ID */
  export namespace ProviderId {
    export const SNCC = 'sncc';
    export const FVNO_11FV = '11fv';
    export const FVNO_12FV = '12fv';
    export const FVNO_13FV = '13fv';
    export const FVNO_1AFV = '1afv';
    export const FVNO_1BFV = '1bfv';
    export const FVNO_1CFV = '1cfv';
    export const FVNO_1EFV = '1efv';
    export const FVNO_1FFV = '1ffv';
    export const FVNO_1GFV = '1gfv';
    export const FVNO_1HFV = '1hfv';
    export const FVNO_1LFV = '1lfv';
    export const FVNO_1MFV = '1mfv';
    export const FVNO_1OFV = '1ofv';
    export const FVNO_1PFV = '1pfv';
    export const FVNO_A1FV = 'a1fv';
    export const FVNO_AKFV = 'akfv';
    export const FVNO_BBFV = 'bbfv';
    export const FVNO_C1FV = 'c1fv';
    export const FVNO_C2FV = 'c2fv';
    export const FVNO_C3FV = 'c3fv';
    export const FVNO_CMFV = 'cmfv';
    export const FVNO_DKFV = 'dkfv';
    export const FVNO_ELFV = 'elfv';
    export const FVNO_FAFV = 'fafv';
    export const FVNO_GXFV = 'gxfv';
    export const FVNO_HMFV = 'hmfv';
    export const FVNO_JPFV = 'jpfv';
    export const FVNO_K1FV = 'k1fv';
    export const FVNO_K2FV = 'k2fv';
    export const FVNO_K3FV = 'k3fv';
    export const FVNO_K4FV = 'k4fv';
    export const FVNO_K5FV = 'k5fv';
    export const FVNO_K7FV = 'k7fv';
    export const FVNO_K8FV = 'k8fv';
    export const FVNO_K9FV = 'k9fv';
    export const FVNO_KAFV = 'kafv';
    export const FVNO_KBFV = 'kbfv';
    export const FVNO_KZFV = 'kzfv';
    export const FVNO_L1FV = 'l1fv';
    export const FVNO_LSFV = 'lsfv';
    export const FVNO_NIFV = 'nifv';
    export const FVNO_OGFV = 'ogfv';
    export const FVNO_PRFV = 'prfv';
    export const FVNO_QRFV = 'qrfv';
    export const FVNO_R1FV = 'r1fv';
    export const FVNO_RXFV = 'rxfv';
    export const FVNO_TRFV = 'trfv';
    export const FVNO_UCFV = 'ucfv';
    export const FVNO_VOFV = 'vofv';
    export const FVNO_WUFV = 'wufv';
    export const FVNO_YYFV = 'yyfv';
    export const FVNO_ZNFV = 'znfv';
    export const FVNO_1QFV = '1qfv';
    export const FVNO_1TFV = '1tfv';
    export const FVNO_C4FV = 'c4fv';
    export const FVNO_1UFV = '1ufv';
  }

  /** FVNO提供形態 */
  export namespace FvnoType {
    /** 既存スキーム */
    export const EXIST = '1';
    /** ORIXスキーム */
    export const ORIX = '2';
  }

  /** 10Gsプランフラグ */
  export namespace TenGsPlanFlg {
    /** 10Gsプランでない */
    export const NO = '0';
    /** 10Gsプランである */
    export const YES = '1';
  }

  /** 通知フラグ */
  export namespace NoticeFlg {
    /** 通知する */
    export const ON = '0';
    /** 通知しない */
    export const OFF = '1';
  }

  /** プラン情報 */
  export namespace PlanInfo {
    /** プランコード */
    export const planCode = {
      /** 一括料金コード */
      BULK_PRICE: ['MB31CS', 'MB32CS'],
    };
  }

  /** 利用料金初期表示行数 */
  export namespace BillingInfo {
    /** 初期表示件数 */
    export const INT_DSP_COUNT = 3;
  }

  /** Token認証コード */
  export namespace TokenNinsyoCode {
    export const TOKEN_CODE = 'Z2rbwyXPeYvSRwQLrRvi66tg8fFrEeZh';
  }

  /** 判定フラグ */
  export namespace JudgeFlg {
    export const ON = '1';
    export const OFF = '0';
  }

  /** 付加サービス */
  export namespace AdditionalService {
    /** パッケージリスト */
    export const PACKAGE_LIST: Package[] = [
      {
        type: '01',
        name: 'パック1',
        referenceMark: true,
        explanation: '(番号表示、番号通知リクエスト、キャッチ電話、着信お断り、着信転送)',
        price: '990円',
      },
      {
        type: '02',
        name: 'パック2',
        referenceMark: true,
        explanation: '(キャッチ電話、着信お断り、着信転送)',
        price: '693円',
      },
      {
        type: '03',
        name: 'パック3',
        referenceMark: false,
        explanation: '(番号表示、番号通知リクエスト、キャッチ電話、着信お断り)',
        price: '880円',
      },
      {
        type: '04',
        name: 'パック4',
        referenceMark: false,
        explanation: '(キャッチ電話、着信お断り)',
        price: '583円',
      },
      {
        type: '05',
        name: 'ベーシックパック',
        referenceMark: false,
        explanation: '(番号表示、番号通知リクエスト)',
        price: '550円',
      },
      {
        type: '00',
        name: '申し込まない',
        referenceMark: false,
        explanation: '',
        price: '',
      },
    ];
    /** 単品リスト */
    export const INDIVIDUALLY_LIST: Individually[] = [
      {
        type: 'numberDisplay',
        id: 'optionNumberDisplayView',
        name: '番号表示',
        referenceMark: false,
        toolTip:
          "着信時に相手の電話番号を電話機等のディスプレイに表示するサービスです。<br />ご利用には「ナンバーディスプレイ」対応機器が必要です。<br />NTT「ナンバー・ディスプレイ」相当のサービスです。<br />詳細は<a href='http://www.so-net.ne.jp/option/phone/nuro_hikari/number_disp.html' target='_blank' rel='noreferrer'>こちら</a>",
        construction: '1,100円',
        price: '440円',
      },
      {
        type: 'numberRequest',
        id: 'optionNumberRequestView',
        name: '番号通知リクエスト',
        referenceMark: true,
        toolTip:
          "電話番号を「通知しない」で電話をかけてきた相手に対し、通信設備上で「メッセージ」にて応答し、着信を規制するサービスです。<br />※「メッセージ」接続の間も発信者には、通話料金がかかります。<br />NTT「ナンバー・リクエスト」相当のサービスです。<br />詳細は<a href='http://www.so-net.ne.jp/option/phone/nuro_hikari/number_req.html' target='_blank' rel='noreferrer'>こちら</a>",
        construction: '1,100円',
        price: '220円',
      },
      {
        type: 'catchPhone',
        id: 'optionCatchPhoneView',
        name: 'キャッチ電話',
        referenceMark: false,
        toolTip:
          "通話中に他の人から電話がかかってきた際、割込み音（ツー・ププッ・ププッ）で着信をお知らせします。このとき電話機のフックボタンを操作することにより、通話中の相手を一時的に保留にして、後からかけてきた相手と通話できるサービスです。※保留中の電話には、通話料金がかかります。<br />NTT「キャッチホン」相当のサービスです。<br />詳細は<a href='http://www.so-net.ne.jp/option/phone/nuro_hikari/catch_tel.html' target='_blank' rel='noreferrer'>こちら</a>",
        construction: '-',
        price: '330円',
      },
      {
        type: 'stopNuisanceCall',
        id: 'optionStopNuisanceCallView',
        name: '着信お断り',
        referenceMark: false,
        toolTip:
          "迷惑電話を受けた直後、契約者が電話から登録操作を行なうことにより、以後、同じ電話番号からかかってきた場合に、通信設備上で「着信お断りメッセージ」に接続し、着信を規制するサービスです。<br />※通信設備上で「メッセージ」接続の間も発信者には、通話料金がかかります。<br />NTT「迷惑電話おことわりサービス」相当のサービスです。<br />詳細は<a href='http://www.so-net.ne.jp/option/phone/nuro_hikari/notice.html' target='_blank' rel='noreferrer'>こちら</a>",
        construction: '1,100円',
        price: '660円',
      },
      {
        type: 'voiceWarp',
        id: 'optionVoiceWarpView',
        name: '着信転送',
        referenceMark: false,
        toolTip:
          "かかってきた電話を、契約者があらかじめ指定した電話番号に転送するサービスです。<br />NTT「ボイスワープ」／「転送でんわ」／「着信転送機能」相当のサービスです。<br />詳細は<a href='http://www.so-net.ne.jp/option/phone/nuro_hikari/transmission.html' target='_blank' rel='noreferrer'>こちら</a>",
        construction: '-',
        price: '550円',
      },
    ];
  }
}
